import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { useTenant } from '../../context/TenantContext';
import api from '../../services/api';
import { cacheGet, cacheSet } from '../../services/apiCache';
import CalibrationTimeline from '../Calibrations/CalibrationTimeline';
import TendenciaErro from '../Calibrations/TendenciaErro';
import EditInstrumentModal from './EditInstrumentModal';

// ─── Abas disponíveis ─────────────────────────────────────────────────────────
function getAbas(instrument) {
  const abas = [
    { id: 'detalhes', rotulo: '📏 Critérios e Pontos' },
    { id: 'dadosTecnicos', rotulo: '🔧 Dados Técnicos' },
    { id: 'historico', rotulo: '📋 Histórico de Calibrações' },
    { id: 'tendencia', rotulo: '📈 Tendência de Erro' },
  ];
  if (instrument?.subInstrumentos?.length > 0) {
    abas.push({ id: 'filhos', rotulo: `🔗 Instrumentos Filhos (${instrument.subInstrumentos.length})` });
  }
  return abas;
}

// ─── P2.3: Indicador de frequência vs dias decorridos ────────────────────────
function IndicadorFrequencia({ instrument }) {
  const freq = instrument.calibrationFrequencyDays;
  const ultima = instrument.lastCalibrationDate;

  if (!freq) {
    return <span className="text-sm font-medium">—</span>;
  }

  // Dias decorridos desde a última calibração
  const diasDecorridos = ultima
    ? Math.floor((new Date() - new Date(ultima)) / (1000 * 60 * 60 * 24))
    : null;

  // Percentual de uso do ciclo
  const pct = diasDecorridos !== null ? Math.min((diasDecorridos / freq) * 100, 100) : 0;

  // Cor da barra conforme progresso
  const corBarra =
    pct >= 100 ? 'bg-red-500' :
    pct >= 80  ? 'bg-amber-500' :
                 'bg-emerald-500';

  const corTexto =
    pct >= 100 ? 'text-red-400' :
    pct >= 80  ? 'text-amber-400' :
                 'text-emerald-400';

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-200">{freq} dias</span>
        {diasDecorridos !== null && (
          <span className={`text-xs font-semibold ${corTexto}`}>
            {diasDecorridos}d decorridos
          </span>
        )}
      </div>
      {diasDecorridos !== null && (
        <>
          <div className="h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${corBarra}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-[10px] text-slate-600 mt-1">
            {pct >= 100
              ? 'Ciclo encerrado — calibração necessária'
              : `${Math.round(pct)}% do ciclo utilizado · ${freq - diasDecorridos} dias restantes`}
          </div>
        </>
      )}
    </div>
  );
}

export default function InstrumentDetail({ instrument, onUpdate, onNovaCalibracao }) {
  const { user } = useAuth();
  const { formatarData } = useTenant();
  const [editing, setEditing] = useState(false);
  const [editandoBasico, setEditandoBasico] = useState(false);
  const [quantities, setQuantities] = useState([]);
  const [abaAtiva, setAbaAtiva] = useState('detalhes');
  const [escalas, setEscalas] = useState([]);
  const [sendingCalib, setSendingCalib] = useState(false);
  const [editandoDadosTecnicos, setEditandoDadosTecnicos] = useState(false);
  const [editingFreq, setEditingFreq] = useState(false);
  const [freqValue, setFreqValue] = useState(instrument.calibrationFrequencyDays ?? '');
  const [showBloqueioModal, setShowBloqueioModal] = useState(false);
  const [motivoBloqueioInput, setMotivoBloqueioInput] = useState('');
  const [bloqueioLoading, setBloqueioLoading] = useState(false);
  const [bloqueioError, setBloqueioError] = useState('');

  // Dados Técnicos editáveis
  const [dtMarca, setDtMarca] = useState(instrument.marca || '');
  const [dtModelo, setDtModelo] = useState(instrument.modelo || '');
  const [dtNumeroDeSerie, setDtNumeroDeSerie] = useState(instrument.numeroDeSerie || '');
  const [dtPartNumber, setDtPartNumber] = useState(instrument.partNumber || '');
  const [dtResolucao, setDtResolucao] = useState(instrument.resolucao || '');
  const [dtExatidao, setDtExatidao] = useState(instrument.exatidao || '');
  const [dtTipoInstrumento, setDtTipoInstrumento] = useState(instrument.tipoInstrumento || '');
  const [dtTipoFaixa, setDtTipoFaixa] = useState(instrument.tipoFaixa || '');
  const [dtModoComparacao, setDtModoComparacao] = useState(instrument.modoComparacao || '');
  const [dtCodigoSuperior, setDtCodigoSuperior] = useState(instrument.codigoEquipamentoSuperior || '');
  const [dtUsageMin, setDtUsageMin] = useState(instrument.usageRange?.min ?? '');
  const [dtUsageMax, setDtUsageMax] = useState(instrument.usageRange?.max ?? '');
  const [dtUsageUnit, setDtUsageUnit] = useState(instrument.usageRange?.unit || '');
  const [dtNominalMin, setDtNominalMin] = useState(instrument.nominalRange?.min ?? '');
  const [dtNominalMax, setDtNominalMax] = useState(instrument.nominalRange?.max ?? '');
  const [dtNominalUnit, setDtNominalUnit] = useState(instrument.nominalRange?.unit || '');
  const [dtSaveError, setDtSaveError] = useState('');

  const { can } = usePermissions();
  const isSupervisor = user?.role === 'supervisor';
  const canEdit = can('instrumentos', 'edit');

  const [qrUrl, setQrUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    const token = localStorage.getItem('token');
    fetch(`https://calibraai-backend.fly.dev/api/v1/instruments/${instrument._id}/qr`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { objectUrl = URL.createObjectURL(blob); setQrUrl(objectUrl); } })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [instrument._id]);

  const handlePrintLabel = () => {
    const w = window.open('', '_blank', 'width=400,height=500');
    const proxData = formatarData(instrument.nextCalibrationDate);
    w.document.write(`<!DOCTYPE html><html><head><title>Etiqueta ${instrument.tag}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #fff; }
  .label { border: 2px solid #000; border-radius: 8px; padding: 14px 16px; width: 220px; text-align: center; }
  .tag { font-size: 22px; font-weight: 900; letter-spacing: 2px; margin-bottom: 4px; }
  .desc { font-size: 9px; color: #444; margin-bottom: 10px; max-height: 28px; overflow: hidden; }
  img { width: 140px; height: 140px; display: block; margin: 0 auto 10px; }
  .row { font-size: 9px; color: #333; margin-top: 3px; }
  .row span { font-weight: bold; }
  .footer { font-size: 8px; color: #888; margin-top: 8px; border-top: 1px solid #ddd; padding-top: 6px; }
</style></head><body>
<div class="label">
  <div class="tag">${instrument.tag}</div>
  <div class="desc">${instrument.description}</div>
  ${qrUrl ? `<img src="${qrUrl}" />` : '<div style="width:140px;height:140px;background:#eee;margin:0 auto 10px;display:flex;align-items:center;justify-content:center;font-size:10px;color:#999">QR indisponível</div>'}
  <div class="row">Setor: <span>${instrument.sector || '—'}</span></div>
  <div class="row">Próx. cal.: <span>${proxData}</span></div>
  ${instrument.instrumentClass?.name ? `<div class="row">Classe: <span>${instrument.instrumentClass.name}</span></div>` : ''}
  <div class="footer">CalibraAI</div>
</div>
<script>window.onload = () => { window.print(); window.close(); };</script>
</body></html>`);
    w.document.close();
  };

  useEffect(() => {
    const key = `/instruments/${instrument._id}`;
    const cached = cacheGet(key);
    if (cached) { setQuantities(cached.quantities || []); return; }
    api.get(key).then(({ data }) => {
      cacheSet(key, data, 30_000);
      setQuantities(data.quantities || []);
    }).catch(() => {});
  }, [instrument._id]);

  useEffect(() => {
    const cached = cacheGet('/escalas');
    if (cached) { setEscalas(cached); return; }
    api.get('/escalas').then((r) => {
      cacheSet('/escalas', r.data, 10 * 60_000);
      setEscalas(r.data);
    }).catch(() => {});
  }, []);

  const [saveError, setSaveError] = useState('');

  const handleEmCalibracao = async () => {
    setSendingCalib(true);
    try {
      await api.patch(`/instruments/${instrument._id}/calibration-status`, { calibrationStatus: 'em_calibracao' });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setSendingCalib(false);
    }
  };

  const handleRevertCalib = async () => {
    setSendingCalib(true);
    try {
      await api.patch(`/instruments/${instrument._id}/calibration-status`, { calibrationStatus: 'revert' });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setSendingCalib(false);
    }
  };

  const handleBloqueioManual = async () => {
    if (!motivoBloqueioInput.trim()) {
      setBloqueioError('Informe o motivo do bloqueio.');
      return;
    }
    setBloqueioLoading(true);
    setBloqueioError('');
    try {
      await api.patch(`/instruments/${instrument._id}/calibration-status`, {
        calibrationStatus: 'bloqueado',
        motivo: motivoBloqueioInput.trim(),
      });
      setShowBloqueioModal(false);
      setMotivoBloqueioInput('');
      if (onUpdate) onUpdate();
    } catch (err) {
      setBloqueioError(err.response?.data?.error || 'Erro ao bloquear instrumento.');
    } finally {
      setBloqueioLoading(false);
    }
  };

  const handleDesbloquear = async () => {
    setBloqueioLoading(true);
    try {
      await api.patch(`/instruments/${instrument._id}/calibration-status`, {
        calibrationStatus: 'revert_bloqueio',
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
    } finally {
      setBloqueioLoading(false);
    }
  };

  const handleSaveFreq = async () => {
    try {
      await api.patch(`/instruments/${instrument._id}`, {
        calibrationFrequencyDays: freqValue !== '' ? parseInt(freqValue) : null,
      });
      setEditingFreq(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveDadosTecnicos = async () => {
    setDtSaveError('');
    try {
      await api.patch(`/instruments/${instrument._id}`, {
        marca: dtMarca || null, modelo: dtModelo || null,
        numeroDeSerie: dtNumeroDeSerie || null, partNumber: dtPartNumber || null,
        resolucao: dtResolucao || null, exatidao: dtExatidao || null,
        tipoInstrumento: dtTipoInstrumento || null, tipoFaixa: dtTipoFaixa || null,
        modoComparacao: dtModoComparacao || null, codigoEquipamentoSuperior: dtCodigoSuperior || null,
        usageRange: dtUsageMin !== '' || dtUsageMax !== ''
          ? { min: parseFloat(dtUsageMin) || 0, max: parseFloat(dtUsageMax) || 0, unit: dtUsageUnit }
          : undefined,
        nominalRange: dtNominalMin !== '' || dtNominalMax !== ''
          ? { min: parseFloat(dtNominalMin) || 0, max: parseFloat(dtNominalMax) || 0, unit: dtNominalUnit }
          : undefined,
      });
      setEditandoDadosTecnicos(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setDtSaveError(err.response?.data?.error || 'Erro ao salvar');
    }
  };

  const handleSave = async () => {
    setSaveError('');
    try {
      await api.put(`/instruments/${instrument._id}`, { quantities });
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    }
  };

  const addQuantity = () => {
    setQuantities([...quantities, {
      name: '',
      unit: '',
      numLeituras: 1,
      estrategiaVarredura: 'asc',
      acceptanceCriteria: { type: 'maxError', value: '', maxError: null },
      calibrationPoints: [],
    }]);
  };
  const updateQuantity = (i, f, v) => { const up = [...quantities]; up[i][f] = v; setQuantities(up); };
  const updateCriteria = (qi, f, v) => {
    const up = [...quantities];
    up[qi].acceptanceCriteria = { ...up[qi].acceptanceCriteria, [f]: v };
    if (f === 'type') up[qi].acceptanceCriteria.maxError = null;
    setQuantities(up);
  };
  const addPoint = (qi) => { const up = [...quantities]; up[qi].calibrationPoints.push({ applied: '' }); setQuantities(up); };
  const removePoint = (qi, pi) => { const up = [...quantities]; up[qi].calibrationPoints.splice(pi, 1); setQuantities(up); };
  const updatePoint = (qi, pi, f, v) => { const up = [...quantities]; up[qi].calibrationPoints[pi][f] = v; setQuantities(up); };
  const removeQuantity = (i) => { if (quantities.length > 1) setQuantities(quantities.filter((_, idx) => idx !== i)); };

  const CRITERIA_LABELS = {
    maxError: 'Erro Máx. (±)',
    fundoDeEscala: 'Fundo de Escala (%FE)',
    percentFundoDeEscala: '% Fundo de Escala',
  };

  const statusLabel = (s) => {
    const map = {
      aprovado: 'Aprovado',
      calibrado: 'Calibrado',
      a_vencer: 'A vencer',
      vencido: 'Vencido',
      bloqueado: 'Bloqueado',
      em_calibracao: 'Em calibração',
      aguardando_laudo: 'Aguardando laudo',
      nao_calibrado: 'Não calibrado',
    };
    return map[s] || s;
  };

  const statusClass = {
    aprovado: 'bg-green-900 text-green-300',
    calibrado: 'bg-green-900 text-green-300',
    a_vencer: 'bg-yellow-900 text-yellow-300',
    vencido: 'bg-red-900 text-red-300',
    bloqueado: 'bg-slate-700 text-slate-300',
    em_calibracao: 'bg-purple-900 text-purple-300',
    aguardando_laudo: 'bg-orange-900 text-orange-300',
    nao_calibrado: 'bg-gray-700 text-gray-400',
  }[instrument.calibrationStatus] || 'bg-gray-700 text-gray-400';

  const motivoBloqueioTexto = () => {
    if (instrument.calibrationStatus !== 'bloqueado') return null;
    if (instrument.motivoBloqueio) return instrument.motivoBloqueio;
    const proxima = instrument.nextCalibrationDate ? new Date(instrument.nextCalibrationDate) : null;
    if (proxima && proxima < new Date()) {
      return `Calibração vencida em ${formatarData(instrument.nextCalibrationDate)}.`;
    }
    return 'Instrumento reprovado na última calibração.';
  };

  return (
    <div className="text-[#e2e8f0]">

      {/* ── Modal edição básica (operador: description, sector, type, status) ── */}
      {editandoBasico && (
        <EditInstrumentModal
          instrument={instrument}
          onClose={() => setEditandoBasico(false)}
          onUpdated={(updated) => {
            setEditandoBasico(false);
            if (onUpdate) onUpdate(updated);
          }}
        />
      )}

      {/* ── Modal de confirmação de bloqueio ───────────────────────────────── */}
      {showBloqueioModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h3 className="text-base font-semibold text-white mb-1">Bloquear instrumento</h3>
            <p className="text-xs text-slate-500 mb-4">
              O instrumento ficará indisponível para uso. Informe o motivo do bloqueio.
            </p>
            <textarea
              value={motivoBloqueioInput}
              onChange={e => { setMotivoBloqueioInput(e.target.value); setBloqueioError(''); }}
              placeholder="Ex: Equipamento aguardando peça de reposição"
              rows={3}
              className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] outline-none focus:border-orange-500 resize-none"
              autoFocus
            />
            {bloqueioError && <p className="text-red-400 text-xs mt-1">{bloqueioError}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setShowBloqueioModal(false); setMotivoBloqueioInput(''); setBloqueioError(''); }}
                className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleBloqueioManual}
                disabled={bloqueioLoading}
                className="px-4 py-2 bg-orange-700 hover:bg-orange-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                {bloqueioLoading ? 'Bloqueando...' : 'Confirmar bloqueio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Banner de instrumento bloqueado ─────────────────────────────────── */}
      {instrument.calibrationStatus === 'bloqueado' && (
        <div className="mb-4 bg-red-950/60 border border-red-700/60 rounded-lg px-4 py-3 flex items-start gap-3">
          <span className="text-red-400 text-xl mt-0.5">🔒</span>
          <div className="flex-1">
            <p className="text-red-300 font-semibold text-sm">Instrumento bloqueado</p>
            <p className="text-red-400/80 text-xs mt-0.5">{motivoBloqueioTexto()}</p>
            {!instrument.motivoBloqueio && (
              <p className="text-red-400/60 text-xs mt-1">
                Para liberar via calibração, registre resultado{' '}
                <span className="font-medium text-red-300">Aprovado</span>.
              </p>
            )}
          </div>
          {isSupervisor && (
            <button
              onClick={handleDesbloquear}
              disabled={bloqueioLoading}
              className="flex-shrink-0 text-xs px-3 py-1.5 bg-red-900/60 border border-red-700/60 text-red-300 rounded-lg hover:bg-red-800/60 transition-colors disabled:opacity-50"
            >
              Desbloquear
            </button>
          )}
        </div>
      )}

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-2xl font-bold font-mono">{instrument.tag}</h2>
          <p className="text-[#94a3b8] mt-1">{instrument.description}</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {canEdit && (instrument.calibratable ?? true) && instrument.calibrationStatus !== 'bloqueado' && (
            <button
              onClick={instrument.calibrationStatus === 'em_calibracao' ? handleRevertCalib : handleEmCalibracao}
              disabled={sendingCalib}
              title={instrument.calibrationStatus === 'em_calibracao' ? 'Clique para reverter' : 'Enviar para calibração externa'}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-all duration-200 disabled:opacity-50 ${
                instrument.calibrationStatus === 'em_calibracao'
                  ? 'bg-violet-700/50 border-violet-500/80 text-violet-200 hover:bg-violet-600/50'
                  : 'bg-transparent border-[var(--surface-4)] text-slate-400 hover:border-violet-700/60 hover:text-violet-300'
              }`}
            >
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 transition-colors ${
                instrument.calibrationStatus === 'em_calibracao' ? 'bg-violet-300' : 'bg-slate-600'
              }`} />
              Em calibração externa
              {instrument.calibrationStatus === 'em_calibracao' && (
                <span className="text-[10px] text-violet-400/70 ml-0.5">✕</span>
              )}
            </button>
          )}
          {isSupervisor && instrument.calibrationStatus !== 'bloqueado' && (
            <button
              onClick={() => { setShowBloqueioModal(true); setBloqueioError(''); }}
              title="Bloquear instrumento manualmente"
              className="flex items-center gap-2 px-3 py-2 border border-[var(--surface-4)] text-slate-400 hover:border-orange-700/60 hover:text-orange-300 rounded-lg text-sm transition-all duration-200"
            >
              🔒 Bloquear
            </button>
          )}
          {onNovaCalibracao && (
            <button
              onClick={onNovaCalibracao}
              className="px-3 py-2 bg-[var(--surface-3)] border border-[var(--surface-4)] text-slate-300 rounded-lg hover:border-blue-600 hover:text-blue-400 text-sm transition-colors"
            >
              + Calibração
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => {
                if (isSupervisor) {
                  setEditing(!editing);
                  setAbaAtiva('detalhes');
                } else {
                  setEditandoBasico(true);
                }
              }}
              className="px-4 py-2 bg-[#1d4ed8] text-[#bfdbfe] rounded-lg hover:bg-[#3b82f6] text-sm"
            >
              {editing ? 'Cancelar' : '✏️ Editar'}
            </button>
          )}
        </div>
      </div>

      {/* ── Cards de informações ────────────────────────────────────────────── */}
      <div className="flex gap-4 mb-6 items-stretch">

        {/* Coluna esquerda: info cards */}
        <div className="flex-1 grid grid-cols-3 gap-3">
          {[
            ['Setor', instrument.sector || '—'],
            ['Classe', instrument.instrumentClass ? (
              <div key="classe">
                <div className="text-sm font-medium">{instrument.instrumentClass.name}</div>
                {(instrument.instrumentClass.calibrationFrequencyDays || instrument.instrumentClass.criterioAceitacao) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {instrument.instrumentClass.calibrationFrequencyDays && (
                      <span className="text-[9px] text-blue-400 bg-blue-950/30 border border-blue-800/40 px-1.5 py-0.5 rounded-full">
                        ⏱ {instrument.instrumentClass.calibrationFrequencyDays}d
                      </span>
                    )}
                    {instrument.instrumentClass.criterioAceitacao && (
                      <span className="text-[9px] text-emerald-400 bg-emerald-950/30 border border-emerald-800/40 px-1.5 py-0.5 rounded-full">
                        ✓ {instrument.instrumentClass.criterioAceitacao}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : '—'],
            ['Status Calibração', (
              <span key="status" className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                {statusLabel(instrument.calibrationStatus)}
              </span>
            )],
            ['Última Calibração', instrument.lastCalibrationDate
              ? new Date(instrument.lastCalibrationDate).toLocaleDateString('pt-BR')
              : '—'],
            ['Próxima Calibração', formatarData(instrument.nextCalibrationDate)],
          ].map(([label, value], i) => (
            <div key={i} className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1">{label}</div>
              <div className="text-sm font-medium">{value}</div>
            </div>
          ))}

          {/* Card: Frequência com indicador de progresso + edição inline */}
          <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-wider text-[#64748b]">Frequência · Ciclo</div>
                {(() => {
                  const classFreq = instrument.instrumentClass?.calibrationFrequencyDays;
                  const instFreq = instrument.calibrationFrequencyDays;
                  if (!classFreq || !instFreq) return null;
                  if (instFreq === classFreq) {
                    return <span className="text-[9px] text-blue-400 bg-blue-950/30 border border-blue-800/40 px-1.5 py-0.5 rounded-full">padrão</span>;
                  }
                  return <span className="text-[9px] text-amber-400 bg-amber-950/30 border border-amber-800/40 px-1.5 py-0.5 rounded-full">personalizado · classe: {classFreq}d</span>;
                })()}
              </div>
              {canEdit && !editingFreq && (
                <button onClick={() => setEditingFreq(true)} className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors">✏️</button>
              )}
            </div>
            {editingFreq ? (
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" value={freqValue}
                  onChange={e => setFreqValue(e.target.value)}
                  placeholder="dias"
                  className="flex-1 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-sm text-[var(--text-base)] outline-none focus:border-blue-500 w-16"
                  autoFocus
                />
                <span className="text-xs text-slate-500">dias</span>
                <button onClick={handleSaveFreq} className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-2 py-1 rounded">✓</button>
                <button onClick={() => { setEditingFreq(false); setFreqValue(instrument.calibrationFrequencyDays ?? ''); }} className="text-xs text-slate-500 hover:text-slate-300">✕</button>
              </div>
            ) : (
              <IndicadorFrequencia instrument={instrument} />
            )}
          </div>
        </div>

        {/* Coluna direita: QR Code */}
        <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-3 flex flex-col items-center justify-between w-36 flex-shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-[#64748b] self-start">QR Code</div>
          {qrUrl ? (
            <img src={qrUrl} alt={`QR ${instrument.tag}`} className="w-24 h-24 rounded" />
          ) : (
            <div className="w-24 h-24 bg-[var(--surface-4)] rounded animate-pulse" />
          )}
          <div className="flex flex-col gap-1.5 w-full">
            <button
              onClick={handlePrintLabel}
              className="w-full text-[11px] text-slate-400 hover:text-slate-200 border border-[var(--surface-4)] hover:border-slate-500 rounded-lg py-1.5 transition-colors"
            >
              🖨 Etiqueta
            </button>
            <a
              href={`/p/${instrument._id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-[11px] text-blue-400 hover:text-blue-300 border border-blue-900/40 hover:border-blue-700/60 rounded-lg py-1.5 transition-colors text-center"
            >
              🔗 Página pública
            </a>
          </div>
        </div>

      </div>

      {/* ── Abas ────────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-[var(--surface-4)] mb-4">
        {getAbas(instrument).map((aba) => (
          <button
            key={aba.id}
            onClick={() => { setAbaAtiva(aba.id); setEditing(false); setEditandoDadosTecnicos(false); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              abaAtiva === aba.id
                ? 'text-blue-400 border-b-2 border-blue-500 bg-[var(--surface-3)]'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      {/* ── Aba: Critérios e Pontos ─────────────────────────────────────────── */}
      {abaAtiva === 'detalhes' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm uppercase tracking-wider text-[#64748b]">
              Grandezas e Pontos de Calibração
            </h3>
            {editing && (
              <button onClick={addQuantity} className="text-xs bg-[#1d4ed8] text-[#bfdbfe] px-2 py-1 rounded">
                + Grandeza
              </button>
            )}
          </div>

          {quantities.map((q, qi) => (
            <div key={qi} className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-4 mb-4">
              {editing ? (
                <div className="space-y-2 mb-3">
                  {/* Linha 1: nome + unidade */}
                  <div className="flex gap-2 items-center">
                    <input
                      list="grandezas-datalist"
                      placeholder="Nome da grandeza (ex: Temperatura)"
                      value={q.name}
                      onChange={(e) => {
                        updateQuantity(qi, 'name', e.target.value);
                        // auto-seleciona primeira unidade do catálogo se o nome bater
                        const escala = escalas.find((es) => es.grandeza === e.target.value);
                        if (escala?.unidades?.length > 0) {
                          updateQuantity(qi, 'unit', escala.unidades[0]);
                        }
                      }}
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-sm flex-1"
                    />
                    {/* Unidade: select se grandeza está no catálogo, input livre caso contrário */}
                    {escalas.find((es) => es.grandeza === q.name)?.unidades?.length > 0 ? (
                      <select
                        value={q.unit || ''}
                        onChange={(e) => updateQuantity(qi, 'unit', e.target.value)}
                        className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-sm w-28"
                      >
                        {escalas.find((es) => es.grandeza === q.name).unidades.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        placeholder="Unidade (ex: °C)"
                        value={q.unit || ''}
                        onChange={(e) => updateQuantity(qi, 'unit', e.target.value)}
                        className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-sm w-24"
                      />
                    )}
                    {quantities.length > 1 && (
                      <button onClick={() => removeQuantity(qi)} className="text-[#f87171] text-xs whitespace-nowrap">Remover</button>
                    )}
                  </div>
                  <datalist id="grandezas-datalist">
                    {escalas.map((es) => <option key={es.id} value={es.grandeza} />)}
                  </datalist>
                  {/* Linha 2: critério de aceitação */}
                  <div className="flex gap-2 items-center flex-wrap">
                    <select
                      value={q.acceptanceCriteria.type || 'maxError'}
                      onChange={(e) => updateCriteria(qi, 'type', e.target.value)}
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-slate-300"
                    >
                      <option value="maxError">Erro Máximo (±)</option>
                      <option value="fundoDeEscala">% Fundo de Escala</option>
                      <option value="percentFundoDeEscala">% Leitura</option>
                    </select>
                    <input
                      type="number"
                      step="any"
                      placeholder="Valor"
                      value={q.acceptanceCriteria.maxError ?? ''}
                      onChange={(e) => updateCriteria(qi, 'maxError', e.target.value === '' ? null : parseFloat(e.target.value))}
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs w-24"
                    />
                    <span className="text-[#64748b] text-xs">
                      {q.acceptanceCriteria.type === 'maxError' ? q.unit || '' : q.acceptanceCriteria.type === 'fundoDeEscala' ? '% FE' : '% leit.'}
                    </span>
                  </div>
                  {/* Linha 3: estratégia de calibração interna */}
                  <div className="flex gap-2 items-center flex-wrap pt-1 border-t border-[var(--surface-4)]/40">
                    <span className="text-xs text-slate-500">Calibração interna:</span>
                    <select
                      value={q.numLeituras || 1}
                      onChange={(e) => updateQuantity(qi, 'numLeituras', parseInt(e.target.value))}
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-slate-300"
                      title="Leituras por ponto"
                    >
                      <option value={1}>1 leitura/ponto</option>
                      <option value={3}>3 leituras/ponto</option>
                      <option value={5}>5 leituras/ponto</option>
                      <option value={10}>10 leituras/ponto</option>
                    </select>
                    <select
                      value={q.estrategiaVarredura || 'asc'}
                      onChange={(e) => updateQuantity(qi, 'estrategiaVarredura', e.target.value)}
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-slate-300"
                      title="Estratégia de varredura"
                    >
                      <option value="asc">↑ Crescente</option>
                      <option value="desc">↓ Decrescente</option>
                      <option value="asc_desc">↑↓ Crescente + Decrescente</option>
                      <option value="desc_asc">↓↑ Decrescente + Crescente</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h4 className="text-base font-semibold">{q.name || 'Grandeza sem nome'}</h4>
                  {q.unit && (
                    <span className="text-xs bg-[#1e3a5f]/60 text-[#93c5fd] px-2 py-0.5 rounded border border-[#1e3a8a]/40">
                      {q.unit}
                    </span>
                  )}
                  {q.acceptanceCriteria.maxError != null && (
                    <span className="text-xs bg-[#1e3a5f] text-[#93c5fd] px-2 py-0.5 rounded">
                      {CRITERIA_LABELS[q.acceptanceCriteria.type] || 'EMP'} ±{q.acceptanceCriteria.maxError}
                      {q.acceptanceCriteria.type === 'maxError' && q.unit ? ` ${q.unit}` : q.acceptanceCriteria.type !== 'maxError' ? '%' : ''}
                    </span>
                  )}
                  {(q.numLeituras > 1 || q.estrategiaVarredura !== 'asc') && (
                    <span className="text-xs bg-[var(--surface-2)] text-slate-400 px-2 py-0.5 rounded border border-[var(--surface-4)]">
                      {{ asc: '↑', desc: '↓', asc_desc: '↑↓', desc_asc: '↓↑' }[q.estrategiaVarredura] || '↑'}
                      {q.numLeituras > 1 ? ` · ${q.numLeituras}×` : ''}
                    </span>
                  )}
                </div>
              )}

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#64748b] uppercase text-xs">
                    <th className="text-left py-1">#</th>
                    <th className="text-left py-1">Valor Aplicado {!editing && q.unit ? `(${q.unit})` : ''}</th>
                    {editing && <th />}
                  </tr>
                </thead>
                <tbody>
                  {q.calibrationPoints.map((p, pi) => (
                    <tr key={pi} className="border-t border-[var(--surface-5)]">
                      <td className="py-1 text-[#64748b] text-xs">{pi + 1}</td>
                      <td className="py-1">
                        {editing ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="any"
                              value={p.applied}
                              onChange={(e) => updatePoint(qi, pi, 'applied', e.target.value)}
                              className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-1 py-0.5 w-24 text-xs"
                            />
                            {q.unit && <span className="text-[#64748b] text-xs">{q.unit}</span>}
                          </div>
                        ) : (
                          <span>{p.applied}{q.unit ? ` ${q.unit}` : ''}</span>
                        )}
                      </td>
                      {editing && (
                        <td className="py-1">
                          <button onClick={() => removePoint(qi, pi)} className="text-[#f87171] text-xs">✕</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {editing && (
                <button onClick={() => addPoint(qi)} className="text-xs text-[#3b82f6] hover:underline mt-2">
                  + Ponto
                </button>
              )}
            </div>
          ))}

          {editing && (
            <div className="mt-4">
              {saveError && (
                <p className="text-red-400 text-xs mb-2">{saveError}</p>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-600 text-sm"
                >
                  Salvar Grandezas
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Aba: Dados Técnicos ─────────────────────────────────────────────── */}
      {abaAtiva === 'dadosTecnicos' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-sm uppercase tracking-wider text-slate-500">Dados Técnicos e Metrológicos</h3>
            {canEdit && (
              <button
                onClick={() => { setEditandoDadosTecnicos(v => !v); setDtSaveError(''); }}
                className="text-xs px-3 py-1.5 bg-[var(--surface-3)] border border-[var(--surface-4)] text-slate-400 rounded-lg hover:text-white hover:border-slate-500 transition-colors"
              >
                {editandoDadosTecnicos ? 'Cancelar' : '✏️ Editar'}
              </button>
            )}
          </div>

          {/* Identificação Técnica */}
          <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Identificação Técnica</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                ['Marca / Fabricante', dtMarca, setDtMarca],
                ['Modelo', dtModelo, setDtModelo],
                ['Número de Série', dtNumeroDeSerie, setDtNumeroDeSerie],
                ['Part Number', dtPartNumber, setDtPartNumber],
              ].map(([label, val, setter]) => (
                <div key={label}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">{label}</p>
                  {editandoDadosTecnicos ? (
                    <input type="text" value={val} onChange={e => setter(e.target.value)}
                      className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-[var(--text-base)] outline-none focus:border-blue-500" />
                  ) : (
                    <p className="text-sm font-medium text-[var(--text-base)]">{val || '—'}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Características Metrológicas */}
          {(instrument.calibratable ?? true) && (
            <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Características Metrológicas</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                {[
                  ['Resolução', dtResolucao, setDtResolucao],
                  ['Exatidão / Classe', dtExatidao, setDtExatidao],
                  ['Cód. Equipamento Superior', dtCodigoSuperior, (v) => setDtCodigoSuperior(v.toUpperCase())],
                ].map(([label, val, setter]) => (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">{label}</p>
                    {editandoDadosTecnicos ? (
                      <input type="text" value={val} onChange={e => setter(e.target.value)}
                        className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-[var(--text-base)] outline-none focus:border-blue-500" />
                    ) : (
                      <p className="text-sm font-medium text-[var(--text-base)]">{val || '—'}</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {[
                  ['Tipo Instrumento', dtTipoInstrumento, setDtTipoInstrumento, [['','— sel —'],['portatil','Portátil'],['fixo','Fixo'],['componente_malha','Comp. Malha']]],
                  ['Tipo de Faixa', dtTipoFaixa, setDtTipoFaixa, [['','— sel —'],['linear','Linear'],['por_relacao','Por Relação'],['inversa','Inversa']]],
                  ['Modo Comparação', dtModoComparacao, setDtModoComparacao, [['','— sel —'],['fixo','Fixo'],['variavel','Variável']]],
                ].map(([label, val, setter, opts]) => (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">{label}</p>
                    {editandoDadosTecnicos ? (
                      <select value={val} onChange={e => setter(e.target.value)}
                        className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-[var(--text-base)] outline-none">
                        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    ) : (
                      <p className="text-sm font-medium text-[var(--text-base)]">{val || '—'}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Faixas */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-2">Faixa de Uso</p>
                  {editandoDadosTecnicos ? (
                    <div className="grid grid-cols-3 gap-1">
                      <input type="number" step="any" placeholder="Min" value={dtUsageMin} onChange={e => setDtUsageMin(e.target.value)} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-[var(--text-base)] outline-none" />
                      <input type="number" step="any" placeholder="Max" value={dtUsageMax} onChange={e => setDtUsageMax(e.target.value)} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-[var(--text-base)] outline-none" />
                      <input type="text" placeholder="Unid." value={dtUsageUnit} onChange={e => setDtUsageUnit(e.target.value)} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-[var(--text-base)] outline-none" />
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-[var(--text-base)]">
                      {dtUsageMin !== '' || dtUsageMax !== '' ? `${dtUsageMin} – ${dtUsageMax} ${dtUsageUnit}` : '—'}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-2">Faixa Nominal</p>
                  {editandoDadosTecnicos ? (
                    <div className="grid grid-cols-3 gap-1">
                      <input type="number" step="any" placeholder="Min" value={dtNominalMin} onChange={e => setDtNominalMin(e.target.value)} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-[var(--text-base)] outline-none" />
                      <input type="number" step="any" placeholder="Max" value={dtNominalMax} onChange={e => setDtNominalMax(e.target.value)} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-[var(--text-base)] outline-none" />
                      <input type="text" placeholder="Unid." value={dtNominalUnit} onChange={e => setDtNominalUnit(e.target.value)} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1 text-xs text-[var(--text-base)] outline-none" />
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-[var(--text-base)]">
                      {dtNominalMin !== '' || dtNominalMax !== '' ? `${dtNominalMin} – ${dtNominalMax} ${dtNominalUnit}` : '—'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {editandoDadosTecnicos && (
            <div>
              {dtSaveError && <p className="text-red-400 text-xs mb-2">{dtSaveError}</p>}
              <div className="flex justify-end">
                <button onClick={handleSaveDadosTecnicos} className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-sm font-medium">
                  Salvar Dados Técnicos
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Aba: Histórico de Calibrações ───────────────────────────────────── */}
      {abaAtiva === 'historico' && (
        <CalibrationTimeline
          instrumentId={instrument._id}
          onNovaCalibracao={onNovaCalibracao}
        />
      )}

      {/* ── Aba: Tendência de Erro (P3.1) ───────────────────────────────────── */}
      {abaAtiva === 'tendencia' && (
        <TendenciaErro instrumentId={instrument._id} />
      )}

      {/* ── Aba: Instrumentos Filhos ─────────────────────────────────────────── */}
      {abaAtiva === 'filhos' && (
        <div className="space-y-2">
          {(instrument.subInstrumentos || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Nenhum instrumento filho cadastrado.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-[var(--surface-4)]">
                  <th className="text-left py-2 px-3">TAG</th>
                  <th className="text-left py-2 px-3">Descrição</th>
                  <th className="text-left py-2 px-3">Setor</th>
                  <th className="text-left py-2 px-3">Classificação</th>
                  <th className="text-left py-2 px-3">Status Calibração</th>
                  <th className="text-left py-2 px-3">Próx. Calibração</th>
                </tr>
              </thead>
              <tbody>
                {instrument.subInstrumentos.map(filho => (
                  <tr key={filho.id} className="border-b border-[var(--surface-4)] hover:bg-[var(--surface-3)] transition-colors">
                    <td className="py-2 px-3 font-mono text-blue-400 text-xs">{filho.tag}</td>
                    <td className="py-2 px-3 text-slate-300">{filho.description}</td>
                    <td className="py-2 px-3 text-slate-400 text-xs">{filho.sector || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${filho.classificacao === 'padrao' ? 'bg-indigo-950/40 border-indigo-800/50 text-indigo-300' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>
                        {filho.classificacao === 'padrao' ? 'Padrão' : 'Instrumento'}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        filho.calibrationStatus === 'calibrado' ? 'bg-emerald-900/40 text-emerald-300' :
                        filho.calibrationStatus === 'vencido' ? 'bg-red-900/40 text-red-300' :
                        filho.calibrationStatus === 'a_vencer' ? 'bg-amber-900/40 text-amber-300' :
                        'bg-slate-800 text-slate-400'
                      }`}>{filho.calibrationStatus || '—'}</span>
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-400">
                      {formatarData(filho.nextCalibrationDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}